import { memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import {
    ClickableIcon,
    HStack,
    IconHide,
    IconPlusContour,
    IconShow,
    IconVerticalMenu,
    Menu,
} from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { useToggle } from '@direct-frontend/stdlib';
import { type OpenApiSchemaCategory } from '#shared/api/schema';
import { checkIsLiteStore } from '#shared/lib/storeInfo';
import styles from './CategoryControls.module.css';

export interface CategoryControlsProps extends TestProps {
    size?: 's' | 'm';
    category: OpenApiSchemaCategory;
    onToggleActionsMenu?: (isOpen: boolean) => void;
    onAddChild: (category: OpenApiSchemaCategory) => void;
    onEdit: (category: OpenApiSchemaCategory) => void;
    onDuplicate: (category: OpenApiSchemaCategory) => void;
    onDelete: (category: OpenApiSchemaCategory) => void;
    onHide: (category: OpenApiSchemaCategory) => void;
}

export const CategoryControls = memo<CategoryControlsProps>(function CategoryControls({
    'data-testid': dataTestId = 'CategoryControls',
    category,
    onToggleActionsMenu,
    onAddChild,
    onEdit,
    onDuplicate,
    onDelete,
    onHide,
    size = 's',
}) {
    const handleCategoryCreateClick = useCallback(() => {
        onAddChild(category);
    }, [category, onAddChild]);

    const handleEdit = useCallback(() => {
        onEdit(category);
    }, [category, onEdit]);

    const handleDuplicate = useCallback(() => {
        onDuplicate(category);
    }, [category, onDuplicate]);

    const handleDelete = useCallback(() => {
        onDelete(category);
    }, [category, onDelete]);

    const handleHide = useCallback(() => {
        onHide(category);
    }, [category, onHide]);

    const isMain = category.is_main;
    const { value: isMenuOpen, toggleOn: openMenu, toggleOff: closeMenu } = useToggle(false);

    const menuItems = useMemo(
        () => [
            {
                key: 'main',
                options: [
                    { value: 'edit', content: 'Редактировать', onAction: handleEdit },
                    { value: 'duplicate', content: 'Дублировать', onAction: handleDuplicate },
                ],
            },
            {
                value: 'delete',
                content: 'Удалить',
                onAction: handleDelete,
                disabled: isMain,
                caption: isMain ? 'Обязательная категория' : undefined,
            },
        ],
        [handleDelete, handleDuplicate, handleEdit, isMain]
    );
    const isLiteStore = checkIsLiteStore();

    return (
        <HStack alignItems="flex-start">
            {isLiteStore ? null : (
                <ClickableIcon
                    title={category.is_hidden_in_menu ? 'Показать категорию' : 'Скрыть категорию'}
                    className={cn(styles.control, { [styles.sizeM]: size === 'm' })}
                    onClick={handleHide}
                >
                    {category.is_hidden_in_menu ? <IconHide size="20" /> : <IconShow size="20" />}
                </ClickableIcon>
            )}
            <ClickableIcon
                title="Добавить дочернюю категорию"
                className={cn(styles.control, { [styles.sizeM]: size === 'm' })}
                onClick={handleCategoryCreateClick}
            >
                <IconPlusContour size="20" />
            </ClickableIcon>
            <Menu
                isOpen={isMenuOpen}
                data-testid={`${dataTestId}.Kebab`}
                aria-label="Контекстное меню"
                onOpenChange={(isOpen) => {
                    onToggleActionsMenu?.(isOpen);
                    isOpen ? openMenu() : closeMenu();
                }}
                size="m"
                items={menuItems}
                placement={['bottom-end', 'top-end']}
                trigger={
                    <ClickableIcon
                        data-testid={`${dataTestId}.Kebab.OpenButton`}
                        className={cn(styles.control, { [styles.sizeM]: size === 'm' })}
                        role="supplementary"
                    >
                        <IconVerticalMenu size="20" />
                    </ClickableIcon>
                }
            />
        </HStack>
    );
});
